import type CameraControls from 'camera-controls'
import { Vector3 } from 'three'

import {
  applyCameraViewToControls,
  captureCameraViewFromControls,
  type CameraView,
} from './cameraView'

/** Altura mínima de la cámara (mm). Sustituye la mutación directa de `camera.position`. */
export const CAMERA_MIN_ELEVATION_Y = 300

export type CameraNavigationMode = 'user' | 'locked' | 'preserved' | 'programmatic'

export type CameraApplyReason =
  | 'user-restore'
  | 'programmatic-fit'
  | 'preservation'
  | 'y-clamp'
  | 'project-restore'

/**
 * Punto único para aplicar vistas programáticas.
 * Cancela interpolaciones activas antes de teletransportes instantáneos.
 */
export function applyCameraViewSafely(
  controls: CameraControls,
  view: CameraView,
  options: { animate?: boolean; reason?: CameraApplyReason } = {},
): void {
  const animate = options.animate ?? false
  if (!animate) {
    controls.stop()
  }
  applyCameraViewToControls(controls, view, animate)
}

/**
 * Impide que la cámara baje del suelo virtual usando la API de camera-controls
 * (no mutar `PerspectiveCamera.position` directamente).
 */
export function clampCameraElevationViaControls(
  controls: CameraControls,
  camera: { position: Vector3 },
  minY: number = CAMERA_MIN_ELEVATION_Y,
): boolean {
  if (camera.position.y >= minY) return false

  controls.stop()
  controls.setPosition(camera.position.x, minY, camera.position.z, false)
  controls.update()
  return true
}

/** Congela la vista actual en los controles (p. ej. inicio de arrastre). */
export function captureFrozenCameraView(
  controls: CameraControls,
  camera: { position: Vector3 },
): CameraView {
  return captureCameraViewFromControls(controls, camera)
}

/** Resuelve el modo de navegación a partir del estado de la app. */
export function resolveCameraNavigationMode(input: {
  isModalOpen: boolean
  isDraggingItem: boolean
  itemDragActive: boolean
  cameraViewPreservationDepth: number
  shouldFitCameraToItems: boolean
}): CameraNavigationMode {
  if (input.isModalOpen || input.isDraggingItem || input.itemDragActive) {
    return 'locked'
  }
  if (input.cameraViewPreservationDepth > 0) {
    return 'preserved'
  }
  if (input.shouldFitCameraToItems) {
    return 'programmatic'
  }
  return 'user'
}

export function isCameraNavigationLocked(mode: CameraNavigationMode): boolean {
  return mode === 'locked'
}

/** Umbral de drift en preservation (mm). Evita correcciones frame-a-frame demasiado agresivas. */
export const CAMERA_PRESERVATION_DRIFT_EPSILON = 8

/** Bloquea fit/restore programático mientras hay gesto de arrastre activo. */
export function shouldBlockProgrammaticCameraView(input: {
  isDraggingItem: boolean
}): boolean {
  return input.isDraggingItem
}
